import { TbPlayerPlayFilled, TbVolume } from "solid-icons/tb";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { VIDEOS } from "~/components/marketing/content";
import { useI18n } from "~/i18n";
import { type ActiveVideo, useMarketingVideo } from "./context";
import { DockControls } from "./DockControls";
import { useDock } from "./useDock";

/**
 * The marketing pages' one <video>. It must be the first child of the scrolling page content,
 * and that wrapper must be positioned: inline, the player is laid over its slot inside it.
 */
export const VideoPlayer = () => {
  const video = useMarketingVideo();
  return (
    <Show when={video.active()} keyed>
      {(active) => <Player active={active} />}
    </Show>
  );
};

const Player = (props: { active: ActiveVideo }) => {
  const { t } = useI18n();
  const ctx = useMarketingVideo();
  let box!: HTMLDivElement;
  let video!: HTMLVideoElement;
  const slot = () => {
    const current = ctx.slot();
    return current?.id === props.active.id ? current.el : undefined;
  };
  useDock(() => box, slot);
  const [muted, setMuted] = createSignal(props.active.fromLink);
  const [prompt, setPrompt] = createSignal<"play" | "sound">();
  const start = () => video.play().catch(() => setPrompt("play"));

  onMount(() => {
    if (!props.active.fromLink) {
      // Synchronous, inside the click that opened the player: Safari only allows sound then.
      void start();
      video.focus({ preventScroll: true });
      slot()?.scrollIntoView({ block: "nearest" });
      return;
    }
    // A frame later, so it lands after the layout's scroll-to-top on load.
    const frame = requestAnimationFrame(() => {
      slot()?.scrollIntoView({ block: "center" });
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return setPrompt("play");
      video.play().then(
        () => setPrompt("sound"),
        () => setPrompt("play")
      );
    });
    onCleanup(() => cancelAnimationFrame(frame));
  });

  return (
    <div
      ref={box}
      role="region"
      aria-label={t(VIDEOS[props.active.id].title)}
      onKeyDown={(e) => {
        if (e.key === "Escape") ctx.close();
      }}
      class="group/player absolute z-20 origin-top-left overflow-hidden rounded-2xl bg-black shadow-xl ring-1 shadow-black/10 ring-black/10 data-docked:fixed data-docked:inset-e-4 data-docked:bottom-4 data-docked:z-40 data-docked:w-[min(24rem,60vw)] data-docked:rounded-xl data-docked:shadow-2xl data-docked:shadow-black/30"
      style={{ "aspect-ratio": `${VIDEOS[props.active.id].width} / ${VIDEOS[props.active.id].height}` }}
    >
      <video
        ref={video}
        src={VIDEOS[props.active.id].src}
        poster={VIDEOS[props.active.id].poster}
        controls
        playsinline
        muted={props.active.fromLink}
        onVolumeChange={() => {
          setMuted(video.muted);
          if (!video.muted && prompt() === "sound") setPrompt(undefined);
        }}
        onPlay={() => {
          if (prompt() === "play") setPrompt(undefined);
        }}
        class="size-full object-contain"
      />
      <Show when={prompt() === "play"}>
        <button
          type="button"
          aria-label={t("marketing.video.play")}
          onClick={() => {
            video.muted = false;
            void start();
          }}
          class="absolute inset-0 grid cursor-pointer place-items-center bg-black/30"
        >
          <span class="grid size-16 place-items-center rounded-full bg-white/95 text-brand shadow-lg group-data-docked/player:size-11">
            <TbPlayerPlayFilled class="size-7 translate-x-0.5 group-data-docked/player:size-5" aria-hidden="true" />
          </span>
        </button>
      </Show>
      <Show when={prompt() === "sound"}>
        <button
          type="button"
          onClick={() => {
            video.muted = false;
          }}
          class="absolute inset-0 m-auto inline-flex size-fit cursor-pointer items-center gap-2 rounded-full bg-white/95 px-5 py-2.5 text-base font-semibold text-brand shadow-lg group-data-docked/player:px-3 group-data-docked/player:py-1.5 group-data-docked/player:text-sm"
        >
          <TbVolume class="size-5" aria-hidden="true" />
          {t("marketing.video.tap_for_sound")}
        </button>
      </Show>
      <DockControls
        muted={muted()}
        onToggleMute={() => {
          video.muted = !video.muted;
        }}
        onClose={ctx.close}
      />
    </div>
  );
};
